import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTable, MatTableDataSource } from "@angular/material/table";
import { ActivatedRoute, Router } from "@angular/router";
import { AccountUserGroupService } from "src/app/account-user-group/account-user-group.service";
import { AvailableAccount } from "src/app/accounts/AvailableAccount";
import { NotificationService } from "src/app/notifications/notification.service";
import { SessionService } from "src/app/session.service";
import { AreYouSureDialogComponent } from "src/app/shared/dialogs/are-you-sure-dialog/are-you-sure-dialog.component";
import { FilterTableDialogComponent } from "src/app/shared/dialogs/filter-table-dialog/filter-table-dialog.component";
import { UiService } from "src/app/ui/ui.service";
import { pageSizeOptions } from "src/app/shared/utils/pagination";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { UtilService } from "src/app/util/util.service";
import { LocalStorageService } from "src/app/storage/local-storage.service";
@Component({
  selector: "app-user-groups-list",
  templateUrl: "./user-groups-list.component.html",
  styleUrls: ["./user-groups-list.component.scss"],
})
export class UserGroupsListComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  pageSizeOptions = pageSizeOptions;
  userGroups!: any;
  dataSource!: MatTableDataSource<unknown>;
  searchValue!: string;

  columns = [
    { code: "name", name: "Group Name", filter: true, selected: true },
    { code: "members", name: "# Members", filter: true, selected: true },
    { code: "created_at", name: "Creation Date", filter: true, selected: true },
    {
      code: "created_by_name",
      name: "Created By",
      filter: true,
      selected: true,
    },
    {
      code: "modified_at",
      name: "Last Modified",
      filter: true,
      selected: true,
    },
    {
      code: "modified_by_name",
      name: "Modified By",
      filter: true,
      selected: true,
    },
    { code: "options" },
  ];
  displayedColumns = this.columns.map((column) => column.code);

  @ViewChild("groupsTable") groupsTable!: MatTable<any[]>;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  showEdit!: boolean;
  showNewGroup!: boolean;
  showDelete!: boolean;
  private onComponentDestroy = new Subject();
  perPage: number = 20;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sessionService: SessionService,
    private uiService: UiService,
    private dialog: MatDialog,
    private accountUserGroupService: AccountUserGroupService,
    private notificationService: NotificationService,
    private utilSvc: UtilService,
    private localStorageSvc: LocalStorageService,
  ) {}

  ngOnInit(): void {
    this.perPage = this.localStorageSvc.pageSize || 20;
    this.userGroups = this.route.snapshot.data.userGroups.records;
    this.dataSource = new MatTableDataSource(this.userGroups);

    this.dataSource.filterPredicate = (data: any, filter: string) => {
      let found = false;
      const selectedFilterColumns = this.columns
        .filter((column) => column.selected)
        .map((column) => column.code);

      for (const column of selectedFilterColumns) {
        let rowValue = data[column];
        if (typeof rowValue === "number") {
          rowValue = rowValue.toString();
        }
        if (rowValue) {
          if (rowValue?.trim().toLowerCase().indexOf(filter) !== -1) {
            found = true;
          }
        }
      }

      return found;
    };
  }

  ngAfterViewInit() {
    this.groupsTable.renderRows();
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  get currentAccount(): AvailableAccount {
    return this.sessionService.currentAccountValue!;
  }

  formatDate = (date: Date) => {
    return this.utilSvc.formatDate(date);
  }

  toggleFilterDialog() {
    this.dialog
      .open(FilterTableDialogComponent, {
        data: {
          columns: this.columns,
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((result) => {
        if (result) {
          this.columns = result;
          this.applyFilter();
        }
      });
  }

  delete(group: any) {
    this.dialog
      .open(AreYouSureDialogComponent, {
        data: {
          areYouSureInfo: {
            description: `Are you sure you want to delete this user group ( ${group.name} )?`,
            warning: "Warning: this is not reversible!",
          },
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((result) => {
        if (result) {
          this.uiService
            .waitForEvent(
              this.accountUserGroupService.deleteAccountUserGroup(
                this.currentAccount.accountID,
                group,
              ),
            )
            .pipe(takeUntil(this.onComponentDestroy))
            .subscribe(
              () => {
                this.uiService
                  .waitForEvent(
                    this.accountUserGroupService.getAccountUserGroups(
                      this.currentAccount.accountID,
                    ),
                  )
                  .pipe(takeUntil(this.onComponentDestroy))
                  .subscribe((groups) => {
                    this.userGroups = (groups as any).records;
                    this.sessionService.updateGroups(this.userGroups);

                    this.dataSource.data = this.userGroups;
                    this.groupsTable.renderRows();
                  });
                this.notificationService.openSuccessSnackBar(
                  `Deleted user group ${group.name} successfully`,
                );
              },
              (err) => {
                const errorMessage =
                  err.error.message ?? "Error deleting user group";
                this.notificationService.openErrorSnackBar(errorMessage);
              },
            );
        }
      });
  }

  edit(group: any) {
    this.router.navigate([group.id], {
      relativeTo: this.route.parent,
    });
  }

  applyFilter() {
    this.dataSource.filter = this.searchValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  copy(group: any) {
    this.uiService
      .waitForEvent(
        this.accountUserGroupService.createAccountUserGroup(
          this.currentAccount.accountID,
          { ...group, id: null, name: group.name + " copy" },
        ),
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        () => {
          this.uiService
            .waitForEvent(
              this.accountUserGroupService.getAccountUserGroups(
                this.currentAccount.accountID,
              ),
            )
            .pipe(takeUntil(this.onComponentDestroy))
            .subscribe((result) => {
              this.userGroups = (result as any).records;
              this.dataSource.data = this.userGroups;
              this.groupsTable.renderRows();
            });
          this.notificationService.openSuccessSnackBar(
            `Group ${group.name} copied successfully`,
          );
        },
        (err) => {
          const errorMessage = err.error.message ?? "Error copying user group";
          this.notificationService.openErrorSnackBar(errorMessage);
        },
      );
  }

  createNewGroup() {
    this.router.navigate(["create"], {
      relativeTo: this.route.parent,
    });
  }
  
  onPageChange(e: PageEvent){
    this.localStorageSvc.pageSize = e.pageSize;
    this.perPage = e.pageSize;
  }
}
